/**
 * Reviewer comments side panel.
 *
 * Sticks to the right of the draft preview. Contents:
 *   - Filter toggle (All | Open | Resolved) with counts
 *   - "Add general comment" button (page-level anchor)
 *   - Composer that opens when `pendingAnchor` is set (from ReviewLayer)
 *   - Threaded comment list with reply forms
 *   - Per-comment Resolve/Reopen + audit history disclosure
 *
 * All state lives in the ReviewContext; this component is presentational.
 */
import { useState } from "react";
import {
  CheckCircle2, CircleDot, Reply, Clock, X, ChevronDown, ChevronRight,
  MessageSquare, Filter,
} from "lucide-react";
import { useReview } from "./ReviewContext";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
}

function anchorLabel(anchor) {
  if (!anchor) return "General";
  if (anchor.startsWith("h2:") || anchor.startsWith("h3:")) return `Section: ${anchor.split(":")[1].replace(/-/g," ")}`;
  if (anchor.startsWith("sel:")) return `Selection · ${anchor.split(":")[1].replace(/-/g," ")}`;
  if (anchor.startsWith("field:")) return `Field: ${anchor.split(":")[1]}`;
  return anchor;
}

function Composer({ prefill, onSubmit, onCancel, submitting, replyTo }) {
  const [text, setText] = useState("");
  const [label] = useState(prefill?.label || "General comment");
  const excerpt = prefill?.excerpt || "";
  return (
    <form
      className="border border-amber-200 bg-amber-50/60 rounded-lg p-3 space-y-2"
      data-testid={replyTo ? `review-reply-form-${replyTo}` : "review-new-comment-form"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!text.trim()) return;
        onSubmit({
          anchor: prefill?.anchor,
          excerpt,
          text: text.trim(),
          parent_id: replyTo || null,
        });
        setText("");
      }}
    >
      {!replyTo && (
        <div className="text-[11px] uppercase tracking-widest text-amber-800 flex items-center justify-between">
          <span>{label}</span>
          {onCancel ? (
            <button type="button" onClick={onCancel} className="text-amber-800 hover:text-amber-900" aria-label="Cancel">
              <X size={12} />
            </button>
          ) : null}
        </div>
      )}
      {excerpt ? (
        <blockquote className="text-xs text-stone-700 border-l-2 border-amber-300 pl-2 italic line-clamp-3">
          “{excerpt}”
        </blockquote>
      ) : null}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={replyTo ? 2 : 3}
        placeholder={replyTo ? "Write a reply…" : "Write a comment…"}
        className="w-full rounded border border-stone-200 px-2 py-1.5 text-sm outline-none focus:border-amber-400 bg-white"
        data-testid={replyTo ? `review-reply-textarea-${replyTo}` : "review-comment-textarea"}
        autoFocus
      />
      <div className="flex items-center gap-2 justify-end">
        {onCancel ? (
          <button type="button" onClick={onCancel} className="text-xs text-stone-600 hover:text-stone-800">
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          disabled={submitting || !text.trim()}
          className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1 font-semibold disabled:opacity-50"
          data-testid={replyTo ? `review-submit-reply-${replyTo}` : "review-submit-comment"}
        >
          {submitting ? "Saving…" : (replyTo ? "Reply" : "Add comment")}
        </button>
      </div>
    </form>
  );
}

function CommentBlock({ node, isReply = false, onReply, onSetStatus }) {
  const [showHistory, setShowHistory] = useState(false);
  const resolved = node.status === "resolved";
  return (
    <div
      data-testid={`review-comment-${node.id}`}
      data-status={node.status}
      className={
        "rounded-lg border p-3 " +
        (resolved
          ? "border-stone-200 bg-stone-50 opacity-80"
          : "border-amber-200 bg-white")
      }
    >
      <div className="flex items-start gap-2">
        <span className={"mt-0.5 " + (resolved ? "text-stone-400" : "text-amber-600")}>
          {resolved ? <CheckCircle2 size={14} /> : <CircleDot size={14} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-semibold text-stone-900" data-testid={`review-comment-author-${node.id}`}>
              {node.author_name || node.author || "Reviewer"}
            </span>
            <span className="text-stone-500">{fmtDate(node.created_at)}</span>
            <span
              className={
                "inline-block text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 " +
                (resolved
                  ? "bg-stone-200 text-stone-700 border border-stone-300"
                  : "bg-amber-100 text-amber-900 border border-amber-300")
              }
              data-testid={`review-comment-status-${node.id}`}
            >
              {resolved ? "Resolved" : "Open"}
            </span>
          </div>
          {node.text ? (
            <p className={"text-sm mt-1 whitespace-pre-wrap " + (resolved ? "text-stone-600" : "text-stone-900")}>
              {node.text}
            </p>
          ) : null}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-stone-500">
            {!isReply ? (
              <button
                type="button"
                onClick={() => onReply(node.id)}
                className="hover:text-stone-800 inline-flex items-center gap-1"
                data-testid={`review-reply-btn-${node.id}`}
              >
                <Reply size={11} /> Reply
              </button>
            ) : null}
            {!isReply ? (
              resolved ? (
                <button
                  type="button"
                  onClick={() => onSetStatus(node.id, "open")}
                  className="hover:text-stone-800 inline-flex items-center gap-1"
                  data-testid={`review-reopen-btn-${node.id}`}
                >
                  <CircleDot size={11} /> Reopen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onSetStatus(node.id, "resolved")}
                  className="hover:text-stone-800 inline-flex items-center gap-1"
                  data-testid={`review-resolve-btn-${node.id}`}
                >
                  <CheckCircle2 size={11} /> Resolve
                </button>
              )
            ) : null}
            {node.history?.length ? (
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="hover:text-stone-800 inline-flex items-center gap-1"
                data-testid={`review-history-toggle-${node.id}`}
              >
                {showHistory ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <Clock size={11} /> History ({node.history.length})
              </button>
            ) : null}
          </div>
          {showHistory && node.history?.length ? (
            <ul className="mt-2 text-[11px] text-stone-500 space-y-1 border-l-2 border-stone-100 pl-2">
              {node.history.map((h, i) => (
                <li key={`${h.at}-${h.action}-${i}`}>
                  <span className="text-stone-700">{h.action}</span> · {h.actor} · {fmtDate(h.at)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function ReviewCommentsPanel() {
  const {
    threads, counts, loading, filter, setFilter,
    panelOpen, setPanelOpen,
    pendingAnchor, setPendingAnchor,
    addComment, setStatus,
  } = useReview();

  const [showNew, setShowNew] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openComposerFromAnchor = pendingAnchor !== null;

  const doSubmit = async (payload) => {
    setSubmitting(true);
    try {
      await addComment(payload);
      setShowNew(false);
      setReplyTo(null);
    } catch (e) {
      // toast handled inside addComment
    } finally {
      setSubmitting(false);
    }
  };

  if (!panelOpen) {
    return (
      <button
        type="button"
        className="fixed right-4 bottom-24 z-50 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg px-4 py-2 text-sm font-semibold flex items-center gap-2"
        onClick={() => setPanelOpen(true)}
        data-testid="review-panel-toggle-open"
      >
        <MessageSquare size={16} /> Comments{counts.open ? ` (${counts.open})` : ""}
      </button>
    );
  }

  return (
    <aside
      className="vv-review-panel w-full lg:w-[360px] lg:max-w-[92vw] shrink-0 flex flex-col bg-white border-t lg:border-t-0 lg:border-l border-stone-200"
      data-testid="review-comments-panel"
    >
      <div className="p-4 border-b border-stone-200 flex items-center gap-2">
        <div className="font-display text-lg text-stone-900">Reviewer comments</div>
        <button
          type="button"
          className="ml-auto text-stone-500 hover:text-stone-800"
          onClick={() => setPanelOpen(false)}
          data-testid="review-panel-close"
          aria-label="Hide panel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-4 pt-3 pb-2 border-b border-stone-100 flex items-center gap-2 text-xs">
        <Filter size={12} className="text-stone-500" />
        {[
          { k: "all", label: `All (${counts.all})` },
          { k: "open", label: `Open (${counts.open})` },
          { k: "resolved", label: `Resolved (${counts.resolved})` },
        ].map((opt) => (
          <button
            key={opt.k}
            type="button"
            onClick={() => setFilter(opt.k)}
            data-testid={`review-filter-${opt.k}`}
            className={
              "rounded-full px-2.5 py-1 border transition " +
              (filter === opt.k
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-600 border-stone-200 hover:border-stone-300")
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="p-3 border-b border-stone-100">
        {openComposerFromAnchor ? (
          <Composer
            prefill={pendingAnchor}
            onSubmit={doSubmit}
            onCancel={() => setPendingAnchor(null)}
            submitting={submitting}
          />
        ) : showNew ? (
          <Composer
            prefill={{ anchor: "field:general", label: "General comment" }}
            onSubmit={doSubmit}
            onCancel={() => setShowNew(false)}
            submitting={submitting}
          />
        ) : (
          <button
            type="button"
            className="w-full text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1.5 font-semibold"
            onClick={() => setShowNew(true)}
            data-testid="review-add-general-comment"
          >
            Add general comment
          </button>
        )}
        <div className="mt-2 text-[11px] text-stone-500 leading-snug">
          Tip: hover a heading to anchor a comment to that section, or select
          text in the draft to comment on a specific passage.
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading ? (
          <div className="text-stone-500 text-sm" data-testid="review-comments-loading">Loading comments…</div>
        ) : threads.length === 0 ? (
          <div className="text-stone-500 text-sm text-center py-8" data-testid="review-comments-empty">
            {counts.all === 0
              ? "No comments yet. Highlight text or click a heading to add one."
              : "No comments match this filter."}
          </div>
        ) : (
          threads.map(({ root, replies }) => (
            <div key={root.id} className="space-y-2" data-testid={`review-thread-${root.id}`}>
              {root.anchor ? (
                <div className="text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
                  <span data-testid={`review-anchor-${root.id}`}>{anchorLabel(root.anchor)}</span>
                </div>
              ) : null}
              {root.excerpt ? (
                <blockquote className="text-xs text-stone-600 border-l-2 border-stone-200 pl-2 italic line-clamp-3">
                  “{root.excerpt}”
                </blockquote>
              ) : null}
              <CommentBlock
                node={root}
                onReply={(id) => setReplyTo(id)}
                onSetStatus={setStatus}
              />
              {replies.length ? (
                <div className="pl-4 border-l border-stone-100 space-y-2">
                  {replies.map((r) => (
                    <CommentBlock key={r.id} node={r} isReply onReply={() => {}} onSetStatus={() => {}} />
                  ))}
                </div>
              ) : null}
              {replyTo === root.id ? (
                <div className="pl-4">
                  <Composer
                    replyTo={root.id}
                    onSubmit={doSubmit}
                    onCancel={() => setReplyTo(null)}
                    submitting={submitting}
                  />
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
